import React, {useEffect, useState} from "react";

import {AppContext} from "../../HomePage";
import {SiteSearchSummaryService} from "../../service/SiteSearchSummaryService";
import {toDateTimeString} from "../../utils/TimeUtil";

const service = new SiteSearchSummaryService();

// Display order is the order a customer reads the answer to "why so few suggestions?":
// where suggestions came from, where the product was found but is already tracked,
// where the site had nothing, and where the site could not be reached.
const GROUPS = [
    {status: 'suggested', tone: 'green'},
    {status: 'nothing_new', tone: 'gray'},
    {status: 'zero_results', tone: 'amber'},
    {status: 'unreachable', tone: 'red'},
];
const MAX_KEYWORDS_SHOWN = 3;

// Which sites a product was searched on and how many results each returned. Every site
// links to its own search page, so the customer can see the result (or the empty page)
// for themselves. Renders nothing for a product that was never searched on a site.
const SiteSearchSummary = ({productId, target, hasSuggestions}) => {

    const {t} = React.useContext(AppContext);
    const [rows, setRows] = useState([]);
    const [expanded, setExpanded] = useState(false);

    useEffect(() => {
        if (!productId) return;
        setRows([]);
        service.getProductSiteSearchSummary(productId, target)
            .then(result => setRows(result || []))
            // An informational card: when it cannot load, the tab works without it.
            .catch(() => setRows([]));
    }, [productId, target]); // eslint-disable-line react-hooks/exhaustive-deps

    // With no undecided suggestions to review, the site list IS the answer, so it starts open.
    useEffect(() => setExpanded(!hasSuggestions), [productId, hasSuggestions]);

    if (!rows.length) return null;

    const label = (key, options) => t(`site_search_summary.${key}`, options);
    const byStatus = (status) => rows.filter(row => row.status === status);
    const totalFound = rows.reduce((sum, row) => sum + (row.found || 0), 0);
    const totalSuggested = rows.reduce((sum, row) => sum + (row.suggested || 0), 0);
    const keywords = [...new Set(rows.map(row => row.keyword).filter(Boolean))];
    const lastSearchedAt = rows.map(row => row.searched_at).filter(Boolean).sort().pop();

    const siteChip = (row, tone) => (
        <a key={row.host} href={row.url} target="_blank" rel="noopener noreferrer"
           className={`pux-ss-chip pux-ss-${tone}`}
           title={label('open_site', {host: row.host, keyword: row.keyword || ''})}>
            {row.host}
            {row.status !== 'unreachable' &&
                <b>{label('results', {count: row.found || 0})}</b>}
            <i className="pi pi-external-link"/>
        </a>
    );

    return <div className="pux-ss">
        <div className="pux-ss-head">
            <i className="pi pi-search pux-ss-icon"/>
            <div className="pux-ss-title">
                <div className="pux-ss-headline">
                    {label('headline', {sites: rows.length, found: totalFound, suggested: totalSuggested})}
                </div>
                <div className="pux-ss-sub">
                    {keywords.length > 0 && <>
                        {label('keyword')} <code>{keywords.slice(0, MAX_KEYWORDS_SHOWN).join(', ')}</code>
                        {keywords.length > MAX_KEYWORDS_SHOWN && ` +${keywords.length - MAX_KEYWORDS_SHOWN}`}
                        {' · '}
                    </>}
                    {lastSearchedAt && `${label('last_search')} ${toDateTimeString(lastSearchedAt)}`}
                </div>
            </div>
            <button type="button" className="pux-ss-toggle" onClick={() => setExpanded(!expanded)}>
                {expanded ? label('hide_sites') : label('show_sites')}
                <i className={expanded ? 'pi pi-chevron-up' : 'pi pi-chevron-down'}/>
            </button>
        </div>

        <div className="pux-ss-bar">
            {GROUPS.map(group => {
                const count = byStatus(group.status).length;
                return count > 0 &&
                    <span key={group.status} className={`pux-ss-${group.tone}-fill`} style={{flex: count}}/>;
            })}
        </div>
        <div className="pux-ss-legend">
            {GROUPS.map(group => {
                const count = byStatus(group.status).length;
                return count > 0 && <span key={group.status}>
                    <span className={`pux-ss-dot pux-ss-${group.tone}-fill`}/>
                    {label(`legend_${group.status}`, {count})}
                </span>;
            })}
        </div>

        {expanded && GROUPS.map(group => {
            const groupRows = byStatus(group.status);
            return groupRows.length > 0 && <div key={group.status}>
                <div className="pux-ss-group">{label(`group_${group.status}`)}</div>
                <div className="pux-ss-chips">{groupRows.map(row => siteChip(row, group.tone))}</div>
            </div>;
        })}
    </div>;
};

export default SiteSearchSummary;
